/**
 * firmaTalepListService.js — Firma tarafı "Talepler Listesi" (kart görünümü, stand bazlı)
 *
 * Route: /Firma/Talepler (FirmaTaleplerListPage.jsx)
 * Base URL (future): https://gw.sakagrup.com:8443/api/v1/firmalar/talepler
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 *
 * standTalepService.js standId bazında talep döner. Bu dosya, bu dekoratör
 * firmanın stand roster'ını (standOverviewService.getStandOverviewList — arama/
 * sayfalama zaten orada var, burada tekrarlanmıyor) standTalepService'in dört
 * alt kaynağıyla (Teknik/Hizmet/İş İzin/Ekipman) birleştirip her stand için tek
 * bir özet satır üretir — 2026-07-15 revizyonu öncesi (satır=talep kalemi) yerine
 * satır=stand oldu, bkz. /fromImageToLayout referans kartı.
 *
 * 2026-11-02 (#696): bu kural kalktı — `projectDeclared` hâlâ kayıtta taşınır ama FirmaTaleplerListPage artık onunla kilitlemez (backend #695: Teknik
 * talep proje beyanı olmadan taslak olarak başlar). Aşağıdaki not tarihçe olarak duruyor.
 *
 * 2026-07-23 revizyonu: yeni kural — bir stand için proje bilgisi yüklenmeden
 * (standOverviewService'teki projectDeclared:false, örn. standId 326) talep
 * oluşturulamaz. buildStandSummary artık bu bayrağı da (`projectDeclared`)
 * kayda ekliyor; FirmaTaleplerListPage.jsx bu standlar için normal talep
 * kutucukları yerine kilitli bir "önce proje yükleyin" satırı gösteriyor —
 * FirmaProjelerListPage.jsx'teki "Proje Yükle" satırının aynı kuralın
 * Talepler tarafındaki karşılığı.
 *
 * 2026-08-10 revizyonu (mock→backend reconciliation): bu dosyanın canlı dalı
 * eskiden var olmayan bir sözleşme yoluna (`GET /firmalar/talepler`) çağrı
 * yapıyordu — gerçek backend'de bu yol yok. Doğru karşılığı
 * `GET /api/v1/stands/talepler/ozet` (bkz. docs/guides/FE-StandTalepRequestController-guide.md),
 * tek bir sunucu-taraflı join ile roster + dört alt kaynağı BİRDEN döner (mock'un
 * 1+4N çağrısı yerine 5 sabit sorgu) — bu yüzden forceLive dalı artık
 * getStandOverviewList + standTalepService çağrılarını TEKRARLAMIYOR, doğrudan
 * /ozet'i çağırıp alanları eşliyor (limit/page 1-tabanlı ↔ page/size 0-tabanlı,
 * katilimciFirmaName→firmaName, statusCounts.rejected→denied — bkz. guide'ın
 * tasks-7 cross-reference tablosu). Test verisi: fuar 298, decoratorFirmaId 49858
 * (Örnek Yapı A.Ş., stand 57552'nin dekoratörü — tek dolu stand).
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getStandOverviewList } from './standOverviewService';
import {
  getTeknikTalepler,
  getHizmetTalepleri,
  getIsIzinleri,
  getEkipmanTalebi,
} from './standTalepService';

const BASE = `${JAVA_BACKEND_API_URL}/firmalar/talepler`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

// fuar 298'e seed edilmiş mock fixture'ın dekoratörü (Örnek Yapı A.Ş.) — bu fuarda
// talep verisi taşıyan TEK stand (57552) buna bağlı. FirmaTaleplerListPage.jsx
// selectedFuarId 298/299 olduğunda getCurrentFirmaId()'nin (localStorage user.firmaId,
// yoksa MOCK_DECORATOR_FIRMA_ID=500) yerine bunu kullanır — bkz. o dosyadaki
// isLiveTestFuar notu.
//
// ⚠️ BİLİNÇLİ KISAYOL, kalıcı değil: giriş yapan hesap NE olursa olsun (bkz.
// FirmaTaleplerListPage.jsx'teki isLiveTestFuar), fuar 298/299'da her zaman BU
// dekoratörün roster'ı gösterilir — testfirmamod gibi bir hesap 49858'e gerçekten
// atanmış değil, sadece kodun kendisi decoratorFirmaId'yi buna sabitliyor. Kök neden
// MOCK_DECORATOR_FIRMA_ID ile aynı: `GET /users/{id}` giriş yapan hesabın kendi
// firmaId'sini (varsa) dönmüyor (bkz. FE-README-guide.md, doğrulandı testfirmamod ile
// 2026-08-10). GELECEK: login/user uç noktası gerçek firmaId'yi döndürdüğünde, bu
// sabit yerine giriş yapan kullanıcının KENDİ firmasının (dekoratör olarak atanmışsa)
// roster'ı gösterilmeli — bkz. docs/BACKEND_TODO.md'nin firmaTalepListService bölümü.
export const TEST_DECORATOR_FIRMA_ID = 49858;

async function buildStandSummary(stand) {
  const [teknik, hizmet, isIzin, ekipman] = await Promise.all([
    getTeknikTalepler(stand.id),
    getHizmetTalepleri(stand.id),
    getIsIzinleri(stand.id),
    getEkipmanTalebi(stand.id),
  ]);

  const hizmetVar = hizmet.filter((r) => r.hasRequest);
  const isIzinVar = isIzin.filter((r) => r.hasRequest);
  const ekipmanVar = ekipman?.hasRequest ? ekipman : null;

  // Durum sayaçları sadece onay akışı olan kalemleri kapsar (Teknik Talepler'in
  // status alanı yok, bkz. standTalepService.js dosya başı yorumu) — Bekliyor/
  // Onaylı/Reddedildi (TALEP_STATUS 0/1/2).
  const statused = [...hizmetVar, ...isIzinVar, ...(ekipmanVar ? [ekipmanVar] : [])];
  const statusCounts = {
    pending: statused.filter((r) => r.status === 0).length,
    approved: statused.filter((r) => r.status === 1).length,
    denied: statused.filter((r) => r.status === 2).length,
  };

  return {
    id: stand.id,
    standId: stand.id,
    standName: stand.standName,
    salonName: stand.salonName,
    firmaName: stand.firmaName,
    dekoratorFirmaName: stand.dekoratorFirmaName,
    // standOverviewService'teki projectDeclared:false — proje henüz yüklenmemiş
    // standlar (bkz. standId 326) için liste kartı talep kutucukları yerine
    // "önce proje yükleyin" satırı gösterir (bkz. FirmaTaleplerListPage.jsx).
    projectDeclared: stand.projectDeclared,
    teknik, // her zaman 4 kalem (hasRequest:false dahil) — liste kartında gri gösterilir
    hizmet: hizmetVar,
    isIzin: isIzinVar,
    ekipman: ekipmanVar,
    statusCounts,
  };
}

/**
 * Bu dekoratör firmanın (seçili fuardaki) her standı için bir özet kart — arama/
 * sayfalama standOverviewService.getStandOverviewList'e devredilir, bu fonksiyon
 * sadece o sayfadaki standları talep özetiyle süsler.
 * GET /firmalar/talepler
 */
export async function getFirmaTalepListesi(params = {}, { forceLive = false } = {}) {
  // #425 / #424 (backend #423): canlıda `firmaId` = "bu firmanın o fuarda HER rolde (Katılımcı/Dekoratör/
  // Taşeron) olduğu standlar"; `decoratorFirmaId` yalnızca Dekoratör satırlarıdır. Mock dal decoratorFirmaId'de kalır.
  const { fuarId, decoratorFirmaId, firmaId, search = '', page = 1, limit = 10 } = params;

  if (USE_MOCK_DATA && !forceLive) {
    const standResult = await getStandOverviewList({ fuarId, decoratorFirmaId, search, page, limit });
    const stands = standResult.records || [];
    const records = await Promise.all(stands.map(buildStandSummary));

    return { total: standResult.total, page: standResult.page, limit: standResult.limit, records };
  }

  try {
    const response = await axios.get(`${JAVA_BACKEND_API_URL}/stands/talepler/ozet`, {
      // WAS (until #425): params: { fuarId, decoratorFirmaId, search, page: page - 1, size: limit },
      params: { fuarId, decoratorFirmaId, firmaId, search, page: page - 1, size: limit },
      headers: getAuthHeader(),
    });
    const data = response.data; // { records, total, page, size, totalPages } — 0-tabanlı
    const records = (data.records || []).map((r) => ({
      id: String(r.standId),
      standId: r.standId,
      standName: r.standName,
      salonName: r.salonName,
      firmaName: r.katilimciFirmaName,
      dekoratorFirmaName: r.dekoratorFirmaName,
      projectDeclared: r.projectDeclared,
      teknik: r.teknik,
      hizmet: r.hizmet,
      isIzin: r.isIzin,
      ekipman: r.ekipman,
      statusCounts: {
        pending: r.statusCounts.pending,
        approved: r.statusCounts.approved,
        denied: r.statusCounts.rejected,
      },
    }));
    return { total: data.total, page: data.page + 1, limit: data.size, records };
  } catch (error) {
    console.error('Error fetching firma talep listesi:', error);
    throw error;
  }
}
